import { useState } from "react";
import {
  Box,
  Tab,
  Tabs,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { MainLayoutContainer } from "components/Layout";
import { CustomTabPanel } from "./styles";
import { CompetencyRequirements, EPARequirements, ITERRequirements } from "./components";
import { useRequirementsPortal } from "./useRequirementsPortal";
import { Requirement } from "types/requirement";
import { EntitySelectorWithTabs } from "modals/EntitySelector";
import { getFilteredList } from "utils/Array";
import { REQUIREMENT } from "helpers/constants";
import {
  useAddAcademicYearRequirementMutation,
  useDeleteAcademicYearRequirementMutation,
} from "graphql/__generated__/hooks";
import { useNotifications } from "hooks/useNotifications";
import { NotificationSeverity } from "helpers/constants";
import { RequirementFeedbackStatsPanelCompacted } from "components/Analytics/RequirementFeedbackStatsPanelCompacted";
import { useAuthUser } from "hooks/useAuthUser";
import { ROLES } from "types/user";

export const RequirementPortal = () => {
  const [value, setValue] = useState(0);
  const [openRequirementsSelector, setOpenRequirementsSelector] =
    useState(false);
  const [openChartModal, setOpenChartModal] = useState(false);
  const [chartModalRequirement, setChartModalRequirement] =
    useState<Requirement | null>(null);
  const { notify } = useNotifications();
  const { user } = useAuthUser();

  const {
    EPAs,
    competencies,
    iters,
    shouldShowRequirementsSelectorButton,
    availableRequirements,
    currentAcademicYearId,
    currentRequirements,
    refetchStudentRequirements,
    feedbackStatsByRequirementId,
  } = useRequirementsPortal();

  const handleOpenChart = (requirement: Requirement) => {
    setChartModalRequirement(requirement);
    setOpenChartModal(true);
  };

  const handleCloseChartModal = () => {
    setOpenChartModal(false);
    setChartModalRequirement(null);
  };

  const [addAcademicYearRequirement] = useAddAcademicYearRequirementMutation();
  const [deleteAcademicYearRequirement] =
    useDeleteAcademicYearRequirementMutation();

  const handleChange = (_: any, newValue: number) => {
    setValue(newValue);
  };

  const handleSelectRequirement = async (selected: Requirement) => {
    if (!currentAcademicYearId) {
      notify({
        message: "Academic year not found",
        severity: NotificationSeverity.error,
      });
      return;
    }

    // Check if requirement is already added
    const isAlreadyAdded = currentRequirements.some(
      (req) => req.requirement_id === selected.requirement_id,
    );

    if (isAlreadyAdded) {
      notify({
        message: "Requirement already added",
        severity: NotificationSeverity.warning,
      });
      return;
    }

    try {
      await addAcademicYearRequirement({
        variables: {
          objects: [
            {
              academic_year_id: currentAcademicYearId,
              requirement_id: selected.requirement_id,
            },
          ],
        },
      });

      notify({
        message: "Requirement added successfully",
        severity: NotificationSeverity.success,
      });

      // Refetch requirements to update the UI
      if (refetchStudentRequirements) {
        await refetchStudentRequirements();
      }

      setOpenRequirementsSelector(false);
    } catch (error) {
      notify({
        message: "Error adding requirement",
        severity: NotificationSeverity.error,
      });
    }
  };

  const handleDeleteRequirement = async (academicYearRequirementId: string) => {
    try {
      await deleteAcademicYearRequirement({
        variables: { id: academicYearRequirementId },
      });

      notify({
        message: "Requirement removed successfully",
        severity: NotificationSeverity.success,
      });

      // Refetch requirements to update the UI
      if (refetchStudentRequirements) {
        await refetchStudentRequirements();
      }
    } catch (error) {
      notify({
        message: "Error removing requirement",
        severity: NotificationSeverity.error,
      });
    }
  };

  return (
    <MainLayoutContainer title="Requirements Portal">
      <Box mt={2}>
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1}
          sx={{ mb: 2 }}
        >
          {shouldShowRequirementsSelectorButton && (
            <Button
              variant="contained"
              onClick={() => setOpenRequirementsSelector(true)}
            >
              Add Requirement
            </Button>
          )}
        </Stack>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="Requirement portal tabs"
          >
            <Tab
              label="EPA Requirements"
              id="requirement-tab-0"
              aria-controls="simple-tabpanel-0"
            />
            <Tab
              label="Competency Requirements"
              id="requirement-tab-1"
              aria-controls="simple-tabpanel-1"
            />
            <Tab
              label="ITER Requirements"
              id="requirement-tab-2"
              aria-controls="simple-tabpanel-2"
            />
          </Tabs>
          <CustomTabPanel value={value} index={0}>
            <EPARequirements
              requirements={EPAs as Requirement[]}
              onDeleteRequirement={
                shouldShowRequirementsSelectorButton
                  ? handleDeleteRequirement
                  : undefined
              }
              onViewChart={user?.role === ROLES.STUDENT ? handleOpenChart : undefined}
            />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            <CompetencyRequirements
              competencies={competencies as Requirement[]}
              onDeleteRequirement={
                shouldShowRequirementsSelectorButton
                  ? handleDeleteRequirement
                  : undefined
              }
              onViewChart={user?.role === ROLES.STUDENT ? handleOpenChart : undefined}
            />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={2}>
            <ITERRequirements
              iters={iters as Requirement[]}
              onDeleteRequirement={
                shouldShowRequirementsSelectorButton
                  ? handleDeleteRequirement
                  : undefined
              }
              onViewChart={user?.role === ROLES.STUDENT ? handleOpenChart : undefined}
            />
          </CustomTabPanel>
        </Box>
      </Box>

      <EntitySelectorWithTabs
        open={openRequirementsSelector}
        onClose={() => setOpenRequirementsSelector(false)}
        title="Select a Requirement"
        listEntities={getFilteredList(
          availableRequirements,
          currentRequirements,
          "requirement_id",
        )}
        filterCriteria={(search: string, data: any) =>
          data.name.toLowerCase().includes(search.toLowerCase())
        }
        onSelect={handleSelectRequirement}
        displayField="name"
        keyField="requirement_id"
        tabs={[
          {
            label: "EPA",
            filterTabCriteria: (requirement: any) =>
              requirement?.type === REQUIREMENT.REQUIREMENT_EPA,
          },
          {
            label: "Competency",
            filterTabCriteria: (requirement: any) =>
              requirement?.type === REQUIREMENT.REQUIREMENT_COMPETENCY,
          },
          {
            label: "ITER",
            filterTabCriteria: (requirement: any) =>
              requirement?.type === REQUIREMENT.REQUIREMENT_ITER,
          },
        ]}
      />

      <Dialog
        open={openChartModal}
        onClose={handleCloseChartModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { maxHeight: "90vh" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">
            {chartModalRequirement ? `${chartModalRequirement.name} – Score Progress` : "Score Progress"}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleCloseChartModal}
            size="small"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {chartModalRequirement && (() => {
            const reqId = chartModalRequirement.requirement_id;
            if (!reqId) return null;
            const stats = feedbackStatsByRequirementId?.[reqId];
            return stats?.totalFeedbacks > 0 ? (
              <RequirementFeedbackStatsPanelCompacted
                totalFeedbacks={stats.totalFeedbacks}
                currentRate={stats.currentRate}
                data={stats.data}
                onClickDot={() => {}}
              />
            ) : (
              <Typography color="text.secondary">
                No score progress data yet. Complete feedbacks for this requirement to see your progress.
              </Typography>
            );
          })()}
        </DialogContent>
      </Dialog>
    </MainLayoutContainer>
  );
};
